import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("pjp_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ---- Pro-gate 402 + Identity-gate 403 handler -----------------------------
// Any Pro-locked endpoint returns HTTP 402 with a structured `detail`. Rather
// than each caller handling this, we broadcast a single global event so the
// <UpgradeGateModal /> mounted at App root can pop the upsell modal. Callers
// that opt out with `config.silent402 = true` (or header X-Silent-402: 1) get
// the raw rejection with no modal — useful for background probes.
//
// Identity-gate 403 (code=identity_required) fires a separate global event
// picked up by <IdentityGateModal /> so we can route straight to /verify/identity.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const cfg = err?.config || {};
    const silent = cfg.silent402 === true || cfg.headers?.["X-Silent-402"] === "1";
    const detail = err?.response?.data?.detail;
    // Fire the appropriate global modal for structured Pro / Identity gates
    // BEFORE flattening — the modals need the full object.
    if (status === 402 && !silent && detail && typeof detail === "object" && detail.code === "pro_required") {
      window.dispatchEvent(new CustomEvent("pjp:pro-required", { detail }));
    }
    if (status === 403 && !silent && detail && typeof detail === "object" && detail.code === "identity_required") {
      window.dispatchEvent(new CustomEvent("pjp:identity-required", { detail }));
    }
    // FastAPI HTTPException `detail` can be a structured object. Dozens of
    // callers do `toast.error(e?.response?.data?.detail || "Error")` — passing
    // an object into sonner crashes React with "Objects are not valid as a
    // React child". Flatten object detail to its `.message` string so every
    // legacy call site keeps working. The full object is still available
    // at `err.response.data.detail_raw` for callers that want it.
    if (detail && typeof detail === "object") {
      err.response.data.detail_raw = detail;
      err.response.data.detail =
        detail.message || detail.msg || detail.reason || detail.detail || "Something went wrong";
    }
    return Promise.reject(err);
  }
);

/**
 * Safe extractor for axios error → plain-string toast message.
 * Kept for callers that want an explicit helper, but no longer strictly
 * needed — the interceptor above already flattens object `detail` fields.
 */
export function errMsg(err, fallback = "Something went wrong") {
  const d = err?.response?.data?.detail;
  if (typeof d === "string" && d) return d;
  if (d && typeof d === "object") {
    return d.message || d.msg || d.reason || d.detail || fallback;
  }
  return err?.message || fallback;
}
